import { useMemo } from "react"
import { keepPreviousData } from "@tanstack/react-query"

import { api } from "~/utils/api"
import { getStockOutTimesV2 } from "~/utils/calculateStockoutTimes"
import { notBeforeTodayUTC } from "~/utils/datetime"

import { useCompany } from "./useCompany"

/**
 * The stock-out loader rows for planning one order that lands at `orderArrival` (see
 * `effectiveReturnsJoins`). Every caller on a page goes through here so they share one fetch.
 */
export function useStockOutTimesDetails(
  variantIds: string[],
  orderArrival: Date | undefined,
  enabled = true,
) {
  return api.stockOutTimes.getStockOutTimesDetails.useQuery(
    {
      variantIds,
      // An overdue order lands now, as the stock projection already treats it.
      replenishmentArrivalDate: orderArrival && notBeforeTodayUTC(orderArrival),
    },
    // A new arrival date refetches; keep the previous rows meanwhile so the views don't blank.
    { enabled: enabled && variantIds.length > 0, placeholderData: keepPreviousData },
  )
}

export const useStockOutTimesV2 = (
  variantIds: string[],
  expectedArrivalDateOfOrder: Date | undefined = undefined,
  nextOrderArrivalDate: Date | undefined = undefined,
  incomingOrders:
    | {
        skuId: string
        warehouseId: string
        quantity: number
        expectedArrivalDate: Date
      }[]
    | undefined = undefined,
  expectedArrivalDateBySku: Map<string, Date> | undefined = undefined,
  includeExtendedMetrics: boolean = false,
  nextOrderArrivalDateBySku: Map<string, Date> | undefined = undefined,
  ignoreLostSales: boolean = false,
) => {
  const { data: stockOutTimesDetails } = useStockOutTimesDetails(
    variantIds,
    // Per-SKU arrivals have no single order to plan around.
    expectedArrivalDateBySku ? undefined : expectedArrivalDateOfOrder,
  )
  const { data: company } = useCompany()

  return useMemo(() => {
    if (company && stockOutTimesDetails)
      return getStockOutTimesV2(
        company.overstockThresholdDays,
        stockOutTimesDetails,
        expectedArrivalDateOfOrder,
        nextOrderArrivalDate,
        incomingOrders,
        expectedArrivalDateBySku,
        false,
        includeExtendedMetrics,
        nextOrderArrivalDateBySku,
        ignoreLostSales,
      )
    else return undefined
  }, [
    stockOutTimesDetails,
    company,
    expectedArrivalDateOfOrder,
    nextOrderArrivalDate,
    incomingOrders,
    expectedArrivalDateBySku,
    includeExtendedMetrics,
    nextOrderArrivalDateBySku,
    ignoreLostSales,
  ])
}

export type SuggestedOrder = {
  skuId: string
  warehouseId: string
  quantity: number
  expectedArrivalDate: Date
  orderDate: Date
}
